/**
 * MeinRegal Page
 * 
 * Zeigt die Manga-Sammlung des Users
 * Verwendet die neue API v1.2 mit cursor-basierter Pagination
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './MeinRegal.module.css';
import { libraryApi, getMediaUrl } from '../api';
import { useConfirm } from '../context/ConfirmContext';
import MangaDetailModal from '../components/common/MangaDetailModal';

const SORT_LABEL_KEYS = {
  '': 'meinRegal.sortOptions.default',
  TITLE_ASC: 'meinRegal.sortOptions.titleAsc',
  TITLE_DESC: 'meinRegal.sortOptions.titleDesc',
  RECENTLY_ADDED: 'meinRegal.sortOptions.recentlyAdded',
  NEXT_RELEASE: 'meinRegal.sortOptions.nextRelease',
  COLLECTION_PROGRESS: 'meinRegal.sortOptions.collectionProgress',
  READING_PROGRESS: 'meinRegal.sortOptions.readingProgress',
  MISSING_BANDS: 'meinRegal.sortOptions.missingBands',
};

const FILTER_LABEL_KEYS = {
  '': 'meinRegal.filterOptions.all',
  COMPLETE: 'meinRegal.filterOptions.complete',
  INCOMPLETE: 'meinRegal.filterOptions.incomplete',
  UNREAD: 'meinRegal.filterOptions.unread',
  NEW_RELEASES: 'meinRegal.filterOptions.newReleases',
  MISSING_BANDS: 'meinRegal.filterOptions.missingBands',
  WISHLIST: 'meinRegal.filterOptions.wishlist',
};

function MeinRegal() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [searchTerm, setSearchTerm] = useState('');
  const [sort, setSort] = useState('');
  const [filter, setFilter] = useState('');
  const [mangas, setMangas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal State
  const [selectedManga, setSelectedManga] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  /**
   * Alle Mangas laden (mit Cursor-Following, server-seitig sortiert/gefiltert)
   */
  const fetchMangas = useCallback(async (activeSort, activeFilter) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await libraryApi.getAllMangas({ sort: activeSort, filter: activeFilter });

      if (result.success) {
        // Mangas für Anzeige transformieren
        const items = (result.items || []).map((m) => ({
          mangaId: m.manga_id,
          heading: m.title || t('meinRegal.unknownTitle'),
          coverUrl: getMediaUrl(m.cover_media_key),
          raw: m,
        }));
        setMangas(items);
      } else {
        // Leere Library ist kein Fehler
        if (result.error?.code === 'NOT_FOUND') {
          setMangas([]);
        } else {
          throw new Error(result.error?.message || t('meinRegal.errorLoad'));
        }
      }
    } catch (e) {
      setMangas([]);
      setError(e?.message || t('meinRegal.errorLoadCollection'));
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Beim Mount und bei Sort-/Filter-Änderung neu laden
  useEffect(() => {
    fetchMangas(sort, filter);
  }, [fetchMangas, sort, filter]);

  /**
   * Client-seitige Filterung
   */
  const filteredMangas = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return mangas;
    return mangas.filter((m) => (m.heading || '').toLowerCase().includes(q));
  }, [mangas, searchTerm]);

  /**
   * Manga-Karte anklicken -> Modal öffnen
   */
  const handleCardClick = (manga) => {
    setSelectedManga(manga);
    setIsModalOpen(true);
  };

  /**
   * Modal schließen
   */
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedManga(null);
  };

  /**
   * Manga aus Sammlung löschen
   */
  const handleDeleteManga = async (e, manga) => {
    e.stopPropagation();

    const ok = await confirm(t('meinRegal.deleteConfirm', { title: manga.heading }), { danger: true });
    if (!ok) return;

    try {
      const result = await libraryApi.removeManga(manga.mangaId);

      if (result.success) {
        setMangas((prev) => prev.filter((m) => m.mangaId !== manga.mangaId));
      } else {
        throw new Error(result.error?.message || t('meinRegal.errorDelete'));
      }
    } catch (err) {
      setError(err?.message || t('meinRegal.errorDelete'));
    }
  };

  /**
   * Callback wenn Manga im Modal gelöscht wurde
   */
  const handleMangaDeleted = (deletedMangaId) => {
    setMangas((prev) => prev.filter((m) => m.mangaId !== deletedMangaId));
    handleCloseModal();
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <input
          type="text"
          placeholder={t('meinRegal.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.searchBar}
        />

        <div className={styles.statsInfo}>
        </div>
      </div>

      <div className={styles.filterBar}>
        <div className={styles.filterGroup}>
          <label htmlFor="sort-select">{t('meinRegal.sortLabel')}</label>
          <select
            id="sort-select"
            className={styles.filterSelect}
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            {libraryApi.SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{t(SORT_LABEL_KEYS[opt.value] || 'meinRegal.sortOptions.default')}</option>
            ))}
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label htmlFor="filter-select">{t('meinRegal.filterLabel')}</label>
          <select
            id="filter-select"
            className={styles.filterSelect}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            {libraryApi.FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{t(FILTER_LABEL_KEYS[opt.value] || 'meinRegal.filterOptions.all')}</option>
            ))}
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className={styles.loadingState}>
          <svg className={styles.spinner} viewBox="0 0 50 50">
            <circle cx="25" cy="25" r="20" fill="none" strokeWidth="4" />
          </svg>
          <p>{t('meinRegal.loading')}</p>
        </div>
      ) : error ? (
        <div className={styles.errorState}>
          <h2>{t('meinRegal.errorTitle')}</h2>
          <p>{error}</p>
          <button onClick={() => fetchMangas(sort, filter)} className={styles.retryButton} type="button">
            {t('meinRegal.retry')}
          </button>
        </div>
      ) : filteredMangas.length === 0 ? (
        <div className={styles.emptyState}>
          <h2>{mangas.length === 0 ? t('meinRegal.emptyTitleNoMangas') : t('meinRegal.emptyTitleNoResults')}</h2>
          <p>
            {mangas.length === 0
              ? t('meinRegal.emptyTextNoMangas')
              : t('meinRegal.emptyTextNoResults')}
          </p>
        </div>
      ) : (
        <div className={styles.cardContainer}>
          {filteredMangas.map((manga) => (
            <div
              key={manga.mangaId}
              className={styles.card}
              onClick={() => handleCardClick(manga)}
            >
              <button
                className={styles.deleteCardButton}
                onClick={(e) => handleDeleteManga(e, manga)}
                title={t('meinRegal.deleteTitle')}
                type="button"
              >
                🗑
              </button>

              {manga.coverUrl ? (
                <img
                  src={manga.coverUrl}
                  alt={manga.heading}
                  className={styles.cardCover}
                  loading="lazy"
                />
              ) : (
                <div className={styles.cardCoverPlaceholder} />
              )}

              <div className={styles.cardContent}>
                <h3 className={styles.cardHeading}>{manga.heading}</h3>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedManga && (
        <MangaDetailModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          mangaId={selectedManga.mangaId}
          coverUrl={selectedManga.coverUrl}
          mangaTitle={selectedManga.heading}
          onMangaDeleted={handleMangaDeleted}
        />
      )}
    </div>
  );
}

export default MeinRegal;
